'use client';

import { useTranslations } from 'next-intl';
import { Clock } from 'lucide-react';
import { useRouter } from '@/i18n/navigation';
import { useGiftVoucherStore, SPA_PASS_PRICE_RON } from '@/stores/gift-voucher-store';

export function SpaPassForm() {
  const t = useTranslations('giftVouchers');
  const router = useRouter();
  const setDraft = useGiftVoucherStore((s) => s.setDraft);

  const handleSelect = (entries: 10 | 15) => {
    setDraft({ type: 'spa_pass', entries, valueRon: SPA_PASS_PRICE_RON[entries] });
    router.push('/gift-vouchers/checkout');
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <SpaPassCard
        entries={10}
        label={t('spa.tenEntries')}
        description={t('spa.tenDescription')}
        validity={t('spa.tenValidity')}
        priceRon={SPA_PASS_PRICE_RON[10]}
        onSelect={() => handleSelect(10)}
        ctaLabel={t('proceedToPayment')}
      />
      <SpaPassCard
        entries={15}
        label={t('spa.fifteenEntries')}
        description={t('spa.fifteenDescription')}
        validity={t('spa.fifteenValidity')}
        priceRon={SPA_PASS_PRICE_RON[15]}
        onSelect={() => handleSelect(15)}
        ctaLabel={t('proceedToPayment')}
        highlight
        highlightLabel={t('spa.bestValue')}
      />
    </div>
  );
}

function SpaPassCard({
  label,
  description,
  validity,
  priceRon,
  onSelect,
  ctaLabel,
  highlight,
  highlightLabel,
}: {
  entries: 10 | 15;
  label: string;
  description: string;
  validity: string;
  priceRon: number;
  onSelect: () => void;
  ctaLabel: string;
  highlight?: boolean;
  highlightLabel?: string;
}) {
  return (
    <div className="relative bg-gradient-to-br from-[#478B8B] to-[#356666] rounded-3xl p-6 sm:p-8 text-white overflow-hidden">
      {highlight && highlightLabel && (
        <div className="absolute top-6 right-6 px-3 py-1 rounded-full bg-[#daa520] text-xs font-bold uppercase tracking-wider text-white">
          {highlightLabel}
        </div>
      )}
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10" />
      <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-white/5" />

      <div className="relative">
        <h3 className="font-poppins text-3xl font-bold mb-2 break-words">{label}</h3>
        <p className="text-white/70 text-sm mb-6">{description}</p>

        <div className="mb-2">
          <span className="text-5xl font-bold">{priceRon}</span>
          <span className="text-xl ml-1">RON</span>
        </div>
        <p className="text-white/60 text-xs mb-6 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          {validity}
        </p>

        <button
          onClick={onSelect}
          className="w-full py-4 rounded-2xl bg-white text-[#478B8B] font-semibold text-lg hover:bg-white/90 active:scale-[0.98] transition-all duration-200 cursor-pointer"
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}
